import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Box, Flex, Text, Button, Heading, HStack, VStack, useToast, Badge, Spinner, Progress,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    FormControl, FormLabel, FormErrorMessage, Input, NumberInput, NumberInputField, Select, Checkbox,
    useDisclosure, IconButton, Tooltip, Divider, Alert, AlertIcon, AlertDescription,
    Table, Thead, Tbody, Tr, Th, Td, Tabs, TabList, TabPanels, Tab, TabPanel,
    SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, StatArrow,
    Stack, Switch, Code, Tag, Link, InputGroup, InputLeftElement, useColorModeValue,
} from "@chakra-ui/react";
import { Select as ChakraReactSelect } from "chakra-react-select";
import { RepeatIcon, AddIcon, DownloadIcon, SearchIcon, SettingsIcon } from "@chakra-ui/icons";
import { Activity, BarChart3, ListChecks, Settings as SettingsIc, Play, X, ExternalLink } from "lucide-react";
import {
    comparadorDashboard, comparadorGetConfig, comparadorSaveConfig,
    comparadorListarFornecedores, comparadorListarMarcas, comparadorBuscarProdutos,
    comparadorPreviewSync, comparadorDispatchSync, comparadorListarSyncs,
    comparadorDetalhesSync, comparadorStatusSync, comparadorCancelarSync,
    comparadorListarItensSync, comparadorListarResultadosItem, comparadorExportarCsvUrl,
    comparadorHistoricoProduto,
} from "../services/api";
import ScrollToTop from "../components/ScrollToTop";

const STATUS_COLOR = {
    pendente: "gray", em_processamento: "blue", concluido: "green",
    falhou: "red", cancelado: "orange",
};
const STATUS_LABEL = {
    pendente: "Pendente", em_processamento: "Processando", concluido: "Concluído",
    falhou: "Falhou", cancelado: "Cancelado",
};
const ITEM_STATUS_COLOR = {
    pendente: "gray", processando: "blue", concluido: "green",
    sem_resultados: "yellow", erro: "red",
};

const fmtMoney = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};
const fmtPct = (v, digits = 1) => {
    if (v === null || v === undefined || v === "") return "—";
    return `${Number(v).toFixed(digits)}%`;
};
const fmtNum = (v) => {
    if (v === null || v === undefined) return "0";
    return Number(v).toLocaleString("pt-BR");
};
const fmtDateTime = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString("pt-BR"); } catch { return s; }
};
const fmtDuration = (segundos) => {
    if (!segundos) return "—";
    const s = Math.round(segundos);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const MOTIVO_INVAL_LABEL = {
    sem_preco: "Sem preço",
    similaridade_baixa: "Nome diferente",
    marca_divergente: "Marca diferente",
    outlier: "Outlier (preço fora da faixa)",
};

// Converte string com vírgula/quebras em array de domínios (lowercase, sem espaço)
const parseDominios = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (!raw) return [];
    return String(raw)
        .split(/[\n,;]+/)
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
};
const stringifyDominios = (arr) => Array.isArray(arr) ? arr.join("\n") : (arr || "");

// ──────────────────────────── Modal: Nova Sincronização ────────────────────────────
function NovaSincModal({ isOpen, onClose, onCreated, configGlobal }) {
    const toast = useToast();
    const [tipo, setTipo] = useState("lote");
    const [fornecedoresSel, setFornecedoresSel] = useState([]); // [{value, label}]
    const [marcasSel, setMarcasSel] = useState([]);             // [{value, label}]
    const [produtoSel, setProdutoSel] = useState(null);
    const [buscaProduto, setBuscaProduto] = useState("");
    const [produtosAchados, setProdutosAchados] = useState([]);

    const [fornecedores, setFornecedores] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [carregandoMarcas, setCarregandoMarcas] = useState(false);

    // filtros
    const [quantidadeMin, setQuantidadeMin] = useState(configGlobal?.filtro_quantidade_min ?? 1);
    const [precoMin, setPrecoMin] = useState(configGlobal?.filtro_preco_min ?? "");
    const [somenteNaoBloqueados, setSomenteNaoBloqueados] = useState(true);
    const [amostraAleatoria, setAmostraAleatoria] = useState(false);
    const [amostraQuantidade, setAmostraQuantidade] = useState(configGlobal?.max_itens_por_sync ?? 1000);

    // override
    const [maxResSerper, setMaxResSerper] = useState(configGlobal?.max_resultados_serper ?? 40);
    const [simNomeMin, setSimNomeMin] = useState(configGlobal?.similaridade_nome_min ?? 60);
    const [simMarcaMin, setSimMarcaMin] = useState(configGlobal?.similaridade_marca_min ?? 70);
    const [exigirMarca, setExigirMarca] = useState(!!configGlobal?.exigir_marca_match);
    const [dominiosBloqRaw, setDominiosBloqRaw] = useState(stringifyDominios(configGlobal?.dominios_bloqueados));
    const [outlierAplicar, setOutlierAplicar] = useState(configGlobal?.outlier_aplicar !== false);
    const [outlierMaxMult, setOutlierMaxMult] = useState(configGlobal?.outlier_max_multiplicador ?? 3);
    const [outlierMinDiv, setOutlierMinDiv] = useState(configGlobal?.outlier_min_divisor ?? 3);

    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmando, setConfirmando] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        comparadorListarFornecedores().then(r => setFornecedores(r.data || []));
    }, [isOpen]);

    // Marcas filtradas pelo conjunto de fornecedores selecionados
    useEffect(() => {
        if (!isOpen) return;
        const ids = fornecedoresSel.map(f => f.value);
        // Endpoint aceita 1 fornecedor por chamada; quando houver multi, busca por cada e faz merge
        const fetchAll = async () => {
            setCarregandoMarcas(true);
            try {
                if (ids.length === 0) {
                    const r = await comparadorListarMarcas({});
                    setMarcas(r.data || []);
                } else {
                    const results = await Promise.all(
                        ids.map(id => comparadorListarMarcas({ fornecedor_id: id }))
                    );
                    const map = new Map();
                    results.forEach(r => (r.data || []).forEach(m => {
                        const cur = map.get(m.nome_fabricante) || 0;
                        map.set(m.nome_fabricante, cur + Number(m.total));
                    }));
                    const merged = Array.from(map.entries())
                        .map(([nome_fabricante, total]) => ({ nome_fabricante, total }))
                        .sort((a, b) => a.nome_fabricante.localeCompare(b.nome_fabricante));
                    setMarcas(merged);
                }
            } finally { setCarregandoMarcas(false); }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, fornecedoresSel]);

    // Quando a lista de marcas muda, remove as selecionadas que nao existem mais
    useEffect(() => {
        if (!marcasSel.length || !marcas.length) return;
        const validNomes = new Set(marcas.map(m => m.nome_fabricante));
        const filtradas = marcasSel.filter(m => validNomes.has(m.value));
        if (filtradas.length !== marcasSel.length) {
            setMarcasSel(filtradas);
            setPreview(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marcas]);

    useEffect(() => {
        const tid = setTimeout(() => {
            if (buscaProduto.length >= 2) {
                comparadorBuscarProdutos(buscaProduto).then(r => setProdutosAchados(r.data || []));
            }
        }, 350);
        return () => clearTimeout(tid);
    }, [buscaProduto]);

    const fornecedorOptions = useMemo(
        () => fornecedores.map(f => ({ value: f.id, label: f.nome })),
        [fornecedores]
    );
    const marcaOptions = useMemo(
        () => marcas.map(m => ({ value: m.nome_fabricante, label: `${m.nome_fabricante} (${m.total})` })),
        [marcas]
    );

    // Retorna { value, error }
    //   value === null  → campo vazio → usa global
    //   error !== null  → valor inválido (mostra msg, não vai pro payload)
    //   error === null e value !== null → valor válido, vai pro payload
    const checarOverride = (val, min = -Infinity, max = Infinity) => {
        if (val === "" || val === null || val === undefined) {
            return { value: null, error: null };
        }
        const n = Number(val);
        if (!isFinite(n)) return { value: null, error: "valor numérico inválido" };
        if (n < min) return { value: null, error: `mínimo ${min}` };
        if (n > max) return { value: null, error: `máximo ${max}` };
        return { value: n, error: null };
    };

    const overrideChecks = useMemo(() => ({
        maxResSerper:  checarOverride(maxResSerper, 1, 100),
        simNomeMin:    checarOverride(simNomeMin, 0, 100),
        simMarcaMin:   checarOverride(simMarcaMin, 0, 100),
        outlierMaxMult: checarOverride(outlierMaxMult, 0),
        outlierMinDiv:  checarOverride(outlierMinDiv, 0),
    }), [maxResSerper, simNomeMin, simMarcaMin, outlierMaxMult, outlierMinDiv]);

    const temErroOverride = useMemo(
        () => Object.values(overrideChecks).some(c => c.error !== null),
        [overrideChecks]
    );

    const buildPayload = () => {
        const override = {};
        if (overrideChecks.maxResSerper.value !== null) override.max_resultados_serper = overrideChecks.maxResSerper.value;
        if (overrideChecks.simNomeMin.value !== null) override.similaridade_nome_min = overrideChecks.simNomeMin.value;
        if (overrideChecks.simMarcaMin.value !== null) override.similaridade_marca_min = overrideChecks.simMarcaMin.value;
        override.exigir_marca_match = !!exigirMarca;

        const dominios = parseDominios(dominiosBloqRaw);
        if (dominios.length > 0) override.dominios_bloqueados = dominios;

        override.outlier_aplicar = !!outlierAplicar;
        if (overrideChecks.outlierMaxMult.value !== null) override.outlier_max_multiplicador = overrideChecks.outlierMaxMult.value;
        if (overrideChecks.outlierMinDiv.value !== null) override.outlier_min_divisor = overrideChecks.outlierMinDiv.value;

        return {
            tipo,
            referencia_id: tipo === "produto" ? produtoSel?.id : null,
            referencia_marca: null,
            filtros: {
                fornecedores_ids: fornecedoresSel.map(f => f.value),
                marcas: marcasSel.map(m => m.value),
                quantidade_min: quantidadeMin === "" ? null : Number(quantidadeMin),
                preco_min: precoMin === "" ? null : Number(precoMin),
                somente_nao_bloqueados: somenteNaoBloqueados,
                amostra_aleatoria: amostraAleatoria,
                amostra_quantidade: amostraAleatoria ? Number(amostraQuantidade) : null,
            },
            configuracoes_override: override,
        };
    };

    const validar = () => {
        if (tipo === "produto" && !produtoSel) return "Selecione um produto";
        if (tipo === "lote" && fornecedoresSel.length === 0 && marcasSel.length === 0)
            return "Selecione ao menos um fornecedor ou uma marca";
        return null;
    };

    const handlePreview = async () => {
        const err = validar();
        if (err) { toast({ status: "warning", title: err }); return; }
        setPreviewLoading(true);
        setPreview(null);
        try {
            const r = await comparadorPreviewSync(buildPayload());
            setPreview(r.data);
        } catch (e) {
            toast({ status: "error", title: "Erro no preview", description: e?.response?.data?.error || e.message });
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleDispatch = async () => {
        const err = validar();
        if (err) { toast({ status: "warning", title: err }); return; }
        setConfirmando(true);
        try {
            const r = await comparadorDispatchSync(buildPayload());
            toast({ status: "success", title: "Sincronização criada", description: `${r.data.total_itens} itens enfileirados` });
            onCreated?.(r.data);
            onClose();
        } catch (e) {
            toast({ status: "error", title: "Erro ao criar", description: e?.response?.data?.error || e?.response?.data?.message || e.message });
        } finally {
            setConfirmando(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Nova Sincronização</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl>
                            <FormLabel>Tipo de sincronização</FormLabel>
                            <Select value={tipo} onChange={e => { setTipo(e.target.value); setPreview(null); }}>
                                <option value="lote">Lote (combinação de fornecedores e/ou marcas)</option>
                                <option value="produto">Produto Individual</option>
                            </Select>
                        </FormControl>

                        {tipo === "lote" && (
                            <>
                                <FormControl>
                                    <FormLabel>Fornecedores</FormLabel>
                                    <ChakraReactSelect
                                        isMulti
                                        placeholder="Todos (deixe vazio) ou selecione um/vários"
                                        options={fornecedorOptions}
                                        value={fornecedoresSel}
                                        onChange={(opts) => { setFornecedoresSel(opts || []); setPreview(null); }}
                                        closeMenuOnSelect={false}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>
                                        Marcas {carregandoMarcas && <Spinner size="xs" ml={2} />}
                                        <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                                            ({marcas.length} disponíveis · digite para filtrar)
                                        </Text>
                                    </FormLabel>
                                    <ChakraReactSelect
                                        isMulti
                                        placeholder="Todas (deixe vazio) ou selecione uma/várias"
                                        options={marcaOptions}
                                        value={marcasSel}
                                        onChange={(opts) => { setMarcasSel(opts || []); setPreview(null); }}
                                        closeMenuOnSelect={false}
                                        isLoading={carregandoMarcas}
                                    />
                                </FormControl>
                            </>
                        )}

                        {tipo === "produto" && (
                            <FormControl>
                                <FormLabel>Buscar produto (código global ou nome)</FormLabel>
                                <InputGroup>
                                    <InputLeftElement><SearchIcon /></InputLeftElement>
                                    <Input value={buscaProduto} onChange={e => setBuscaProduto(e.target.value)} placeholder="ex: amortecedor cofap" />
                                </InputGroup>
                                {produtosAchados.length > 0 && !produtoSel && (
                                    <Box mt={2} maxH="180px" overflowY="auto" border="1px solid #eee" borderRadius="md">
                                        {produtosAchados.map(p => (
                                            <Box key={p.id} p={2} _hover={{ bg: "gray.50", cursor: "pointer" }}
                                                 onClick={() => { setProdutoSel(p); setProdutosAchados([]); setBuscaProduto(""); setPreview(null); }}>
                                                <Text fontSize="sm" fontWeight="semibold">{p.codigo_global} — {p.nome}</Text>
                                                <Text fontSize="xs" color="gray.500">marca: {p.nome_fabricante || "—"} · estoque: {p.quantidade ?? 0} · venda: {fmtMoney(p.valor_venda)}</Text>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {produtoSel && (
                                    <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                                        <HStack justify="space-between">
                                            <Text fontSize="sm">{produtoSel.codigo_global} — {produtoSel.nome}</Text>
                                            <Button size="xs" onClick={() => setProdutoSel(null)}>Trocar</Button>
                                        </HStack>
                                    </Box>
                                )}
                            </FormControl>
                        )}

                        <Divider />
                        <Heading size="xs">Filtros adicionais</Heading>
                        <SimpleGrid columns={2} spacing={3}>
                            <FormControl>
                                <FormLabel fontSize="sm">Quantidade mínima em estoque</FormLabel>
                                <Input type="number" value={quantidadeMin} onChange={e => { setQuantidadeMin(e.target.value); setPreview(null); }} />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Preço mínimo (R$)</FormLabel>
                                <Input type="number" step="0.01" value={precoMin} onChange={e => { setPrecoMin(e.target.value); setPreview(null); }} placeholder="vazio = sem filtro" />
                            </FormControl>
                        </SimpleGrid>

                        <Checkbox isChecked={somenteNaoBloqueados} onChange={e => { setSomenteNaoBloqueados(e.target.checked); setPreview(null); }}>
                            Somente produtos não bloqueados
                        </Checkbox>

                        <Divider />
                        <HStack>
                            <Heading size="xs" flex={1}>Amostra aleatória (caso passe do limite)</Heading>
                            <Switch isChecked={amostraAleatoria} onChange={e => { setAmostraAleatoria(e.target.checked); setPreview(null); }} />
                        </HStack>
                        {amostraAleatoria && (
                            <FormControl>
                                <FormLabel fontSize="sm">Quantos itens (aleatórios)</FormLabel>
                                <Input type="number" value={amostraQuantidade} onChange={e => { setAmostraQuantidade(e.target.value); setPreview(null); }} />
                            </FormControl>
                        )}

                        <Divider />
                        <Heading size="xs">Configurações desta sincronização (override do global)</Heading>
                        <Text fontSize="xs" color="gray.500">Deixe em branco para usar o valor global. Corrija valores em vermelho antes de iniciar.</Text>
                        <SimpleGrid columns={2} spacing={3}>
                            <FormControl isInvalid={!!overrideChecks.maxResSerper.error}>
                                <FormLabel fontSize="sm">Máx resultados Serper por item (1–100)</FormLabel>
                                <Input type="number" min={1} max={100} step={1}
                                    placeholder={`global: ${configGlobal?.max_resultados_serper ?? 40}`}
                                    value={maxResSerper} onChange={e => setMaxResSerper(e.target.value)} />
                                <FormErrorMessage fontSize="xs">{overrideChecks.maxResSerper.error}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={!!overrideChecks.simNomeMin.error}>
                                <FormLabel fontSize="sm">Similaridade mínima do nome (0–100%)</FormLabel>
                                <Input type="number" min={0} max={100} step="0.01"
                                    placeholder={`global: ${configGlobal?.similaridade_nome_min ?? 60}`}
                                    value={simNomeMin} onChange={e => setSimNomeMin(e.target.value)} />
                                <FormErrorMessage fontSize="xs">{overrideChecks.simNomeMin.error}</FormErrorMessage>
                            </FormControl>
                            <FormControl isDisabled={!exigirMarca} isInvalid={!!overrideChecks.simMarcaMin.error}>
                                <FormLabel fontSize="sm">
                                    Similaridade mínima da marca (0–100%)
                                </FormLabel>
                                <Input type="number" min={0} max={100} step="0.01"
                                    placeholder={`global: ${configGlobal?.similaridade_marca_min ?? 70}`}
                                    value={simMarcaMin} onChange={e => setSimMarcaMin(e.target.value)} />
                                {overrideChecks.simMarcaMin.error
                                    ? <FormErrorMessage fontSize="xs">{overrideChecks.simMarcaMin.error}</FormErrorMessage>
                                    : <Text fontSize="xs" color="gray.500" mt={1}>
                                        {exigirMarca
                                            ? "Resultado é válido se nome bater E (marca igual OU similaridade ≥ valor)."
                                            : "Desativado — qualquer marca é aceita (não filtra por marca)."}
                                      </Text>
                                }
                            </FormControl>
                            <FormControl display="flex" alignItems="center" pt={6}>
                                <Switch isChecked={exigirMarca} onChange={e => setExigirMarca(e.target.checked)} mr={2} />
                                <FormLabel fontSize="sm" m={0}>Exigir marca igual / similar</FormLabel>
                            </FormControl>
                        </SimpleGrid>

                        <Divider />
                        <Heading size="xs">Filtros de qualidade dos resultados</Heading>
                        <FormControl>
                            <FormLabel fontSize="sm">Domínios bloqueados (um por linha)</FormLabel>
                            <Input
                                as="textarea"
                                rows={2}
                                fontFamily="mono"
                                fontSize="sm"
                                minH="64px"
                                py={2}
                                value={dominiosBloqRaw}
                                onChange={e => { setDominiosBloqRaw(e.target.value); setPreview(null); }}
                                placeholder={`global: ${(configGlobal?.dominios_bloqueados || []).join(", ") || "—"}`}
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Ignora resultados cujo source/link contenha estes domínios. Vazio usa o global.
                            </Text>
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                            <Switch isChecked={outlierAplicar} onChange={e => { setOutlierAplicar(e.target.checked); setPreview(null); }} mr={2} />
                            <FormLabel fontSize="sm" m={0}>Aplicar filtro de outlier (preço Nx maior/menor que referência)</FormLabel>
                        </FormControl>
                        <SimpleGrid columns={2} spacing={3}>
                            <FormControl isDisabled={!outlierAplicar} isInvalid={!!overrideChecks.outlierMaxMult.error}>
                                <FormLabel fontSize="sm">Multiplicador máximo (preço &gt; N × ref descarta)</FormLabel>
                                <Input type="number" min={0} step="0.1"
                                    placeholder={`global: ${configGlobal?.outlier_max_multiplicador ?? 3}`}
                                    value={outlierMaxMult} onChange={e => { setOutlierMaxMult(e.target.value); setPreview(null); }} />
                                <FormErrorMessage fontSize="xs">{overrideChecks.outlierMaxMult.error}</FormErrorMessage>
                            </FormControl>
                            <FormControl isDisabled={!outlierAplicar} isInvalid={!!overrideChecks.outlierMinDiv.error}>
                                <FormLabel fontSize="sm">Divisor mínimo (preço &lt; ref ÷ N descarta)</FormLabel>
                                <Input type="number" min={0} step="0.1"
                                    placeholder={`global: ${configGlobal?.outlier_min_divisor ?? 3}`}
                                    value={outlierMinDiv} onChange={e => { setOutlierMinDiv(e.target.value); setPreview(null); }} />
                                <FormErrorMessage fontSize="xs">{overrideChecks.outlierMinDiv.error}</FormErrorMessage>
                            </FormControl>
                        </SimpleGrid>
                        <Text fontSize="xs" color="gray.500">
                            Referência: nosso preço ML &gt; preço Site &gt; mediana. Use 0 em qualquer dos lados para desativar a borda.
                        </Text>

                        {preview && (
                            <Alert status={preview.excede_maximo || preview.abaixo_minimo ? "warning" : "info"}>
                                <AlertIcon />
                                <AlertDescription>
                                    Itens potenciais: <b>{fmtNum(preview.total_potencial)}</b> · efetivos:&nbsp;
                                    <b>{fmtNum(preview.total_efetivo)}</b> · créditos estimados:&nbsp;
                                    <b>{fmtNum(preview.creditos_estimados)}</b>
                                    {preview.excede_maximo && <Text fontSize="xs" mt={1} color="red.600">Excede máximo permitido ({fmtNum(preview.max_itens_por_sync)}). Habilite amostra aleatória.</Text>}
                                    {preview.abaixo_minimo && <Text fontSize="xs" mt={1} color="red.600">Abaixo do mínimo permitido ({fmtNum(preview.min_itens_por_sync)}).</Text>}
                                </AlertDescription>
                            </Alert>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter flexWrap="wrap">
                    {temErroOverride && (
                        <Text w="100%" color="red.600" fontSize="xs" mb={2} textAlign="right">
                            Há valores de override inválidos — corrija antes de prosseguir.
                        </Text>
                    )}
                    <Button variant="ghost" onClick={onClose} mr={2}>Cancelar</Button>
                    <Button onClick={handlePreview} isLoading={previewLoading} mr={2}
                            isDisabled={temErroOverride}>
                        Calcular preview
                    </Button>
                    <Button colorScheme="blue" onClick={handleDispatch} isLoading={confirmando}
                            isDisabled={temErroOverride || !preview || preview.excede_maximo || preview.abaixo_minimo}>
                        Iniciar sincronização
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

// ──────────────────────────── Componente: Tabela de Resultados ────────────────────────────
function TabelaResultados({ resultados, referenciaPreco }) {
    if (!resultados || resultados.length === 0) {
        return (
            <Box py={8} textAlign="center" bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                <Text fontSize="sm" color="gray.500">Sem resultados capturados.</Text>
            </Box>
        );
    }

    const ref = Number(referenciaPreco) || null;

    return (
        <Box overflowX="auto" bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
            <Table size="sm">
                <Thead bg="gray.50">
                    <Tr>
                        <Th w="60px">Pos</Th>
                        <Th>Título</Th>
                        <Th>Loja</Th>
                        <Th isNumeric>Preço</Th>
                        {ref && <Th isNumeric>vs ref.</Th>}
                        <Th isNumeric>Sim.</Th>
                        <Th>Marca</Th>
                        <Th>Status</Th>
                        <Th w="48px" textAlign="center">Link</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {resultados.map(r => {
                        const preco = r.preco !== null && r.preco !== undefined ? Number(r.preco) : null;
                        const diffPct = (ref && preco) ? ((preco - ref) / ref) * 100 : null;
                        const corPreco = diffPct === null ? undefined
                            : (diffPct > 5 ? "red.600" : (diffPct < -5 ? "green.600" : "gray.700"));
                        return (
                            <Tr key={r.id} opacity={r.is_valido ? 1 : 0.65}>
                                <Td>
                                    <Badge colorScheme={r.position && r.position <= 5 ? "blue" : "gray"} variant="subtle">
                                        {r.position || "—"}
                                    </Badge>
                                </Td>
                                <Td maxW="380px">
                                    <Text fontSize="xs" noOfLines={2} title={r.title}>{r.title}</Text>
                                </Td>
                                <Td fontSize="xs" maxW="160px" isTruncated title={r.source}>
                                    {r.source || "—"}
                                </Td>
                                <Td isNumeric fontWeight="semibold" color={corPreco}>
                                    {fmtMoney(preco)}
                                </Td>
                                {ref && (
                                    <Td isNumeric fontSize="xs"
                                        color={diffPct > 0 ? "red.600" : (diffPct < 0 ? "green.600" : "gray.600")}>
                                        {diffPct !== null ? `${diffPct > 0 ? "+" : ""}${fmtPct(diffPct)}` : "—"}
                                    </Td>
                                )}
                                <Td isNumeric fontSize="xs">{fmtPct(r.similaridade_nome)}</Td>
                                <Td fontSize="xs">
                                    {r.marca_match
                                        ? <Tag colorScheme="green" size="sm">match</Tag>
                                        : (r.marca_detectada || <Text as="span" color="gray.400">—</Text>)}
                                </Td>
                                <Td>
                                    {r.is_valido
                                        ? <Badge colorScheme="green">Válido</Badge>
                                        : <Tooltip label={MOTIVO_INVAL_LABEL[r.motivo_invalidacao] || r.motivo_invalidacao || "Não computado"}>
                                              <Badge colorScheme={r.motivo_invalidacao === 'outlier' ? "purple" : "gray"}>
                                                  {r.motivo_invalidacao ? (MOTIVO_INVAL_LABEL[r.motivo_invalidacao] || r.motivo_invalidacao) : "Não"}
                                              </Badge>
                                          </Tooltip>
                                    }
                                </Td>
                                <Td textAlign="center">
                                    {r.link && (
                                        <Link href={r.link} isExternal color="blue.500">
                                            <ExternalLink size={14} />
                                        </Link>
                                    )}
                                </Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </Box>
    );
}

// ──────────────────────────── Card de preço (resumo) ────────────────────────────
function CardResumoPreco({ label, value, helpText, accent }) {
    const bg = accent ? `${accent}.50` : "white";
    const borderColor = accent ? `${accent}.200` : "gray.200";
    return (
        <Stat p={3} bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md">
            <StatLabel fontSize="xs" color="gray.600">{label}</StatLabel>
            <StatNumber fontSize="lg" lineHeight={1.2}>{fmtMoney(value)}</StatNumber>
            {helpText && <StatHelpText fontSize="xs" mb={0} mt={1}>{helpText}</StatHelpText>}
        </Stat>
    );
}

function CardResumoDiff({ label, value, helpText }) {
    const cor = value === null || value === undefined ? "gray.500"
        : (value > 0 ? "red.600" : (value < 0 ? "green.600" : "gray.700"));
    const bg = value === null || value === undefined ? "white"
        : (value > 0 ? "red.50" : (value < 0 ? "green.50" : "gray.50"));
    const borderColor = value === null || value === undefined ? "gray.200"
        : (value > 0 ? "red.200" : (value < 0 ? "green.200" : "gray.200"));
    return (
        <Stat p={3} bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="md">
            <StatLabel fontSize="xs" color="gray.600">{label}</StatLabel>
            <StatNumber fontSize="lg" color={cor} lineHeight={1.2}>
                {value === null || value === undefined ? "—" : `${value > 0 ? "+" : ""}${fmtPct(value)}`}
            </StatNumber>
            {helpText && <StatHelpText fontSize="xs" mb={0} mt={1}>{helpText}</StatHelpText>}
        </Stat>
    );
}

// ──────────────────────────── Histórico do produto ────────────────────────────
function HistoricoSincCard({ h, isAtual, isSelecionado, onClick }) {
    const diff = (h.nosso_preco && h.preco_medio_concorrente)
        ? ((h.nosso_preco - h.preco_medio_concorrente) / h.preco_medio_concorrente) * 100
        : null;
    const corDiff = diff === null ? "gray.500" : (diff > 0 ? "red.600" : (diff < 0 ? "green.600" : "gray.600"));

    return (
        <Box
            onClick={onClick}
            cursor="pointer"
            p={3}
            bg={isSelecionado ? "blue.50" : "white"}
            borderWidth="1px"
            borderColor={isSelecionado ? "blue.400" : "gray.200"}
            borderLeftWidth={isSelecionado ? "4px" : "1px"}
            borderRadius="md"
            transition="all 0.15s"
            _hover={{ bg: isSelecionado ? "blue.50" : "gray.50", borderColor: isSelecionado ? "blue.400" : "gray.300" }}
        >
            <HStack justify="space-between" mb={1.5}>
                <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="700" color="gray.700">#{h.sincronizacao_id}</Text>
                    {isAtual && <Badge colorScheme="blue" fontSize="9px">atual</Badge>}
                    <Badge colorScheme={ITEM_STATUS_COLOR[h.status]} fontSize="9px" variant="subtle">
                        {h.status}
                    </Badge>
                </HStack>
                <Text fontSize="10px" color="gray.500">{h.sinc_tipo}</Text>
            </HStack>

            <Text fontSize="11px" color="gray.500" mb={2}>
                {fmtDateTime(h.processado_em || h.created_at)}
            </Text>

            <SimpleGrid columns={2} spacing={1.5}>
                <Box>
                    <Text fontSize="9px" color="gray.500" textTransform="uppercase">Nosso ML</Text>
                    <Text fontSize="xs" fontWeight="600">{fmtMoney(h.nosso_preco)}</Text>
                </Box>
                <Box>
                    <Text fontSize="9px" color="gray.500" textTransform="uppercase">Mediana mkt</Text>
                    <Text fontSize="xs" fontWeight="600">{fmtMoney(h.mediana_preco_concorrente)}</Text>
                </Box>
                <Box>
                    <Text fontSize="9px" color="gray.500" textTransform="uppercase">Válidos</Text>
                    <Text fontSize="xs" fontWeight="600">
                        {h.resultados_validos ?? 0}<Text as="span" color="gray.400">/{h.total_resultados ?? 0}</Text>
                    </Text>
                </Box>
                <Box>
                    <Text fontSize="9px" color="gray.500" textTransform="uppercase">Diff ML</Text>
                    <Text fontSize="xs" fontWeight="700" color={corDiff}>
                        {diff !== null ? `${diff > 0 ? "+" : ""}${fmtPct(diff)}` : "—"}
                    </Text>
                </Box>
            </SimpleGrid>
        </Box>
    );
}

function HistoricoProduto({ historico, currentSincId }) {
    const toast = useToast();
    const lista = useMemo(() => historico?.historico || [], [historico]);

    // Pré-seleciona: atual > último concluído > primeiro
    const idInicial = useMemo(() => {
        if (!lista.length) return null;
        const atual = lista.find(h => String(h.sincronizacao_id) === String(currentSincId));
        if (atual) return atual.id;
        const concluido = lista.find(h => ['concluido', 'sem_resultados'].includes(h.status));
        if (concluido) return concluido.id;
        return lista[0].id;
    }, [lista, currentSincId]);

    const [selecionadoId, setSelecionadoId] = useState(idInicial);
    const [resultadosCache, setResultadosCache] = useState({});
    const [loadingResultados, setLoadingResultados] = useState(false);

    // atualiza seleção quando lista carrega
    useEffect(() => { if (idInicial && !selecionadoId) setSelecionadoId(idInicial); }, [idInicial, selecionadoId]);

    const itemSelecionado = useMemo(
        () => lista.find(h => h.id === selecionadoId),
        [lista, selecionadoId]
    );

    // Carrega resultados sob demanda. O backend já trouxe ultimo_resultados — usa esse para o ultimo_item.
    useEffect(() => {
        if (!itemSelecionado) return;
        const cacheKey = itemSelecionado.id;
        if (resultadosCache[cacheKey]) return;

        // se for o ultimo_item, usa os resultados já carregados pelo backend
        if (historico?.ultimo_item?.id === itemSelecionado.id && historico?.ultimo_resultados) {
            setResultadosCache(prev => ({ ...prev, [cacheKey]: historico.ultimo_resultados }));
            return;
        }

        // só faz fetch se a sinc tem resultados (concluído ou sem_resultados)
        if (!['concluido', 'sem_resultados'].includes(itemSelecionado.status)) {
            setResultadosCache(prev => ({ ...prev, [cacheKey]: [] }));
            return;
        }

        let cancel = false;
        setLoadingResultados(true);
        comparadorListarResultadosItem(itemSelecionado.sincronizacao_id, itemSelecionado.id)
            .then(r => { if (!cancel) setResultadosCache(prev => ({ ...prev, [cacheKey]: r.data || [] })); })
            .catch(() => { if (!cancel) toast({ status: "error", title: "Falha ao carregar resultados desta sinc" }); })
            .finally(() => { if (!cancel) setLoadingResultados(false); });

        return () => { cancel = true; };
    }, [itemSelecionado, resultadosCache, historico, toast]);

    if (!lista.length) {
        return (
            <Box py={10} textAlign="center" bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.500">Este produto ainda não foi sincronizado.</Text>
            </Box>
        );
    }

    const resultados = itemSelecionado ? (resultadosCache[itemSelecionado.id] || null) : null;
    const referenciaPreco = itemSelecionado
        ? (itemSelecionado.nosso_preco || itemSelecionado.nosso_preco_site || null)
        : null;

    const diffSelMl = (itemSelecionado?.nosso_preco && itemSelecionado?.preco_medio_concorrente)
        ? ((itemSelecionado.nosso_preco - itemSelecionado.preco_medio_concorrente) / itemSelecionado.preco_medio_concorrente) * 100
        : null;

    return (
        <Flex direction={{ base: "column", lg: "row" }} gap={4} align="stretch">
            {/* Lista de syncs (esquerda) */}
            <Box w={{ base: "100%", lg: "320px" }} flexShrink={0}>
                <HStack mb={2} px={1}>
                    <Heading size="xs" color="gray.700">Sincronizações deste produto</Heading>
                    <Badge variant="subtle">{lista.length}</Badge>
                </HStack>
                <VStack align="stretch" spacing={2} maxH="640px" overflowY="auto" pr={1}>
                    {lista.map(h => (
                        <HistoricoSincCard
                            key={h.id}
                            h={h}
                            isAtual={String(h.sincronizacao_id) === String(currentSincId)}
                            isSelecionado={h.id === selecionadoId}
                            onClick={() => setSelecionadoId(h.id)}
                        />
                    ))}
                </VStack>
            </Box>

            {/* Detalhes da sinc selecionada (direita) */}
            <Box flex={1} minW={0}>
                {!itemSelecionado && (
                    <Box py={10} textAlign="center" color="gray.400">Selecione uma sincronização à esquerda</Box>
                )}
                {itemSelecionado && (
                    <VStack align="stretch" spacing={3}>
                        <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                            <HStack justify="space-between" mb={3} flexWrap="wrap" spacing={2}>
                                <Box>
                                    <HStack spacing={2}>
                                        <Heading size="sm" color="gray.800">Sinc #{itemSelecionado.sincronizacao_id}</Heading>
                                        <Badge colorScheme={ITEM_STATUS_COLOR[itemSelecionado.status]}>
                                            {itemSelecionado.status}
                                        </Badge>
                                        {String(itemSelecionado.sincronizacao_id) === String(currentSincId) && (
                                            <Badge colorScheme="blue" variant="solid">atual</Badge>
                                        )}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                        Processado em {fmtDateTime(itemSelecionado.processado_em || itemSelecionado.created_at)}
                                    </Text>
                                </Box>
                                <Text fontSize="xs" color="gray.500">
                                    {itemSelecionado.resultados_validos ?? 0} válido(s) de {itemSelecionado.total_resultados ?? 0}
                                </Text>
                            </HStack>

                            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2}>
                                <CardResumoPreco
                                    label="Nosso ML" value={itemSelecionado.nosso_preco} accent="blue" />
                                <CardResumoPreco
                                    label="Nosso Site" value={itemSelecionado.nosso_preco_site} accent="cyan" />
                                <CardResumoPreco
                                    label="Mediana mkt" value={itemSelecionado.mediana_preco_concorrente} />
                                <CardResumoPreco
                                    label="Min · Max"
                                    value={itemSelecionado.menor_preco_concorrente}
                                    helpText={`máx ${fmtMoney(itemSelecionado.maior_preco_concorrente)}`}
                                />
                                <CardResumoDiff label="ML vs média" value={diffSelMl} />
                            </SimpleGrid>
                        </Box>

                        <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                            <Heading size="xs" mb={3} color="gray.700">Resultados desta sincronização</Heading>
                            {loadingResultados && <HStack justify="center" py={6}><Spinner size="sm" /></HStack>}
                            {!loadingResultados && resultados && (
                                <TabelaResultados resultados={resultados} referenciaPreco={referenciaPreco} />
                            )}
                            {!loadingResultados && !resultados && itemSelecionado.status === 'erro' && (
                                <Alert status="error" fontSize="sm">
                                    <AlertIcon />
                                    <AlertDescription>{itemSelecionado.erro_msg || "Esta sincronização falhou."}</AlertDescription>
                                </Alert>
                            )}
                        </Box>
                    </VStack>
                )}
            </Box>
        </Flex>
    );
}

// ──────────────────────────── Modal: Detalhe do Item ────────────────────────────
function ItemDetalheModal({ isOpen, onClose, sincId, item }) {
    const toast = useToast();
    const [tab, setTab] = useState(0);
    const [resultadosSinc, setResultadosSinc] = useState(null);
    const [historico, setHistorico] = useState(null);
    const [loadingSinc, setLoadingSinc] = useState(false);
    const [loadingHist, setLoadingHist] = useState(false);

    // reset ao trocar de item
    useEffect(() => {
        if (!isOpen) return;
        setTab(0);
        setResultadosSinc(null);
        setHistorico(null);
    }, [isOpen, item?.id]);

    // carrega resultados desta sync ao abrir
    useEffect(() => {
        if (!isOpen || !item) return;
        let cancel = false;
        setLoadingSinc(true);
        comparadorListarResultadosItem(sincId, item.id)
            .then(r => { if (!cancel) setResultadosSinc(r.data || []); })
            .catch(() => { if (!cancel) toast({ status: "error", title: "Falha ao carregar resultados" }); })
            .finally(() => { if (!cancel) setLoadingSinc(false); });
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, sincId, item?.id]);

    // carrega historico sob demanda (lazy)
    useEffect(() => {
        if (!isOpen || !item || tab !== 1 || historico !== null || loadingHist) return;
        let cancel = false;
        setLoadingHist(true);
        comparadorHistoricoProduto(item.produto_id)
            .then(r => { if (!cancel) setHistorico(r.data); })
            .catch(() => { if (!cancel) toast({ status: "error", title: "Falha ao carregar histórico" }); })
            .finally(() => { if (!cancel) setLoadingHist(false); });
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tab, item?.produto_id]);

    if (!item) return null;

    const diffMl = (item.nosso_preco && item.preco_medio_concorrente)
        ? ((item.nosso_preco - item.preco_medio_concorrente) / item.preco_medio_concorrente) * 100
        : null;
    const diffSite = (item.nosso_preco_site && item.preco_medio_concorrente)
        ? ((item.nosso_preco_site - item.preco_medio_concorrente) / item.preco_medio_concorrente) * 100
        : null;

    const referencia = Number(item.nosso_preco) || Number(item.nosso_preco_site) || null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent borderRadius={0}>
                <ModalHeader pb={3} borderBottomWidth="1px">
                    <VStack align="stretch" spacing={2}>
                        <HStack flexWrap="wrap" spacing={2}>
                            <Badge colorScheme={ITEM_STATUS_COLOR[item.status]} fontSize="0.75rem" px={2} py={1}>
                                {item.status}
                            </Badge>
                            <Text fontSize="sm" color="gray.500">·</Text>
                            <Text fontSize="sm" color="gray.600">Item #{item.id}</Text>
                            <Text fontSize="sm" color="gray.500">·</Text>
                            <Text fontSize="sm" color="gray.600">Sincronização #{sincId}</Text>
                            {item.processado_em && (
                                <>
                                    <Text fontSize="sm" color="gray.500">·</Text>
                                    <Text fontSize="sm" color="gray.600">{fmtDateTime(item.processado_em)}</Text>
                                </>
                            )}
                        </HStack>
                        <Heading size="md" fontWeight="600" lineHeight={1.3}>
                            {item.codigo_global && (
                                <Text as="span" color="blue.600" mr={2}>{item.codigo_global}</Text>
                            )}
                            <Text as="span" color="gray.800">{item.produto_nome || "—"}</Text>
                        </Heading>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody bg="gray.50" pt={5}>
                    {item.erro_msg && (
                        <Alert status="error" mb={4} fontSize="sm">
                            <AlertIcon />
                            <AlertDescription>{item.erro_msg}</AlertDescription>
                        </Alert>
                    )}

                    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={5}>
                        <CardResumoPreco
                            label="Nosso preço ML"
                            value={item.nosso_preco}
                            accent="blue"
                            helpText="valor_venda do estoque"
                        />
                        <CardResumoPreco
                            label="Nosso preço Site"
                            value={item.nosso_preco_site}
                            accent="cyan"
                            helpText="calculado pela formula"
                        />
                        <CardResumoPreco
                            label="Mediana mercado"
                            value={item.mediana_preco_concorrente}
                            helpText="resultados validos"
                        />
                        <CardResumoPreco
                            label="Média mercado"
                            value={item.preco_medio_concorrente}
                        />
                        <CardResumoPreco
                            label="Min · Max mercado"
                            value={item.menor_preco_concorrente}
                            helpText={`máx ${fmtMoney(item.maior_preco_concorrente)}`}
                        />
                        <CardResumoDiff
                            label="ML vs média"
                            value={diffMl}
                            helpText={
                                <Text as="span">
                                    Site vs média:&nbsp;
                                    <Text as="span" color={diffSite === null ? "gray.500"
                                        : (diffSite > 0 ? "red.600" : (diffSite < 0 ? "green.600" : "gray.700"))}
                                        fontWeight="semibold">
                                        {diffSite === null ? "—" : `${diffSite > 0 ? "+" : ""}${fmtPct(diffSite)}`}
                                    </Text>
                                </Text>
                            }
                        />
                    </SimpleGrid>

                    <HStack mb={3} fontSize="xs" color="gray.600" flexWrap="wrap" spacing={4}>
                        <Text>
                            <b>{item.resultados_validos ?? 0}</b> válido(s) de <b>{item.total_resultados ?? 0}</b> capturado(s)
                        </Text>
                        {item.produto_id && (
                            <Text color="gray.500">produto_id: {item.produto_id}</Text>
                        )}
                    </HStack>

                    <Tabs index={tab} onChange={setTab} colorScheme="blue" variant="enclosed-colored" bg="white" borderRadius="md" boxShadow="sm">
                        <TabList>
                            <Tab>
                                <HStack spacing={2}>
                                    <ListChecks size={14} />
                                    <Text>Resultados desta sincronização</Text>
                                    {resultadosSinc && (
                                        <Badge ml={1} colorScheme="blue" variant="subtle">
                                            {resultadosSinc.length}
                                        </Badge>
                                    )}
                                </HStack>
                            </Tab>
                            <Tab>
                                <HStack spacing={2}>
                                    <BarChart3 size={14} />
                                    <Text>Histórico do produto</Text>
                                    {historico?.historico && (
                                        <Badge ml={1} variant="subtle">
                                            {historico.historico.length}
                                        </Badge>
                                    )}
                                </HStack>
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel p={4}>
                                {loadingSinc && <HStack justify="center" py={6}><Spinner /></HStack>}
                                {!loadingSinc && resultadosSinc && (
                                    <TabelaResultados resultados={resultadosSinc} referenciaPreco={referencia} />
                                )}
                            </TabPanel>
                            <TabPanel p={4}>
                                {loadingHist && <HStack justify="center" py={6}><Spinner /></HStack>}
                                {!loadingHist && historico && (
                                    <HistoricoProduto
                                        historico={historico}
                                        currentSincId={sincId}
                                    />
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter borderTopWidth="1px" bg="white">
                    <Button onClick={onClose}>Fechar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

// ──────────────────────────── Bloco: Configurações usadas ────────────────────────────
function ConfigsUsadasBlock({ sinc, configGlobal }) {
    if (!sinc?.configuracoes) return null;
    const cfg = sinc.configuracoes;
    const filtros = sinc.filtros || {};

    // Compara cada chave entre sinc e global. Se diferente => "alterado".
    const isDiferente = (k) => {
        const a = cfg[k];
        const b = configGlobal?.[k];
        if (Array.isArray(a) || Array.isArray(b)) {
            return JSON.stringify(a || []) !== JSON.stringify(b || []);
        }
        return Number(a) !== Number(b) && String(a ?? "") !== String(b ?? "");
    };

    const ItemConfig = ({ label, k, render }) => {
        const valor = cfg[k];
        const dif = configGlobal && isDiferente(k);
        return (
            <Box>
                <HStack spacing={1.5} mb={0.5}>
                    <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.4px" fontWeight="600">
                        {label}
                    </Text>
                    {dif && <Badge colorScheme="orange" fontSize="9px" variant="subtle">alterado</Badge>}
                </HStack>
                <Text fontSize="sm" fontWeight={dif ? "700" : "500"} color={dif ? "orange.700" : "gray.700"}>
                    {render ? render(valor) : (valor ?? "—")}
                </Text>
                {dif && configGlobal && (
                    <Text fontSize="10px" color="gray.500">
                        global: {render ? render(configGlobal[k]) : String(configGlobal[k] ?? "—")}
                    </Text>
                )}
            </Box>
        );
    };

    return (
        <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
            <Heading size="xs" mb={3} color="gray.700">Configurações usadas nesta sincronização</Heading>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                <ItemConfig label="Máx Serper" k="max_resultados_serper" />
                <ItemConfig label="Sim. nome mín" k="similaridade_nome_min" render={v => `${v}%`} />
                <ItemConfig label="Sim. marca mín" k="similaridade_marca_min" render={v => `${v}%`} />
                <ItemConfig label="Exigir marca" k="exigir_marca_match" render={v => v ? "Sim" : "Não"} />
                <ItemConfig label="Filtro outlier" k="outlier_aplicar" render={v => v ? "Ativo" : "Desativado"} />
                <ItemConfig label="Mult. máx" k="outlier_max_multiplicador" render={v => `${v}×`} />
                <ItemConfig label="Div. mín" k="outlier_min_divisor" render={v => `${v}×`} />
                <ItemConfig
                    label="Domínios bloq."
                    k="dominios_bloqueados"
                    render={v => Array.isArray(v) && v.length ? `${v.length} dom.` : "—"}
                />
            </SimpleGrid>

            {Array.isArray(cfg.dominios_bloqueados) && cfg.dominios_bloqueados.length > 0 && (
                <Box mb={3}>
                    <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.4px" fontWeight="600" mb={1}>
                        Domínios bloqueados
                    </Text>
                    <HStack flexWrap="wrap" spacing={1}>
                        {cfg.dominios_bloqueados.map(d => (
                            <Tag key={d} size="sm" variant="subtle" colorScheme="gray">{d}</Tag>
                        ))}
                    </HStack>
                </Box>
            )}

            {(filtros.fornecedores_ids?.length || filtros.marcas?.length || filtros.amostra_aleatoria || filtros.quantidade_min || filtros.preco_min) && (
                <>
                    <Divider my={3} />
                    <Heading size="xs" mb={2} color="gray.600">Filtros aplicados</Heading>
                    <HStack flexWrap="wrap" spacing={1.5}>
                        {filtros.fornecedores_ids?.length > 0 && (
                            <Tag size="sm" colorScheme="blue" variant="subtle">
                                {filtros.fornecedores_ids.length} fornecedor(es)
                            </Tag>
                        )}
                        {filtros.marcas?.length > 0 && filtros.marcas.map(m => (
                            <Tag key={m} size="sm" colorScheme="purple" variant="subtle">{m}</Tag>
                        ))}
                        {filtros.quantidade_min != null && filtros.quantidade_min !== "" && (
                            <Tag size="sm" colorScheme="gray" variant="subtle">Qtd. mín: {filtros.quantidade_min}</Tag>
                        )}
                        {filtros.preco_min && (
                            <Tag size="sm" colorScheme="gray" variant="subtle">Preço mín: {fmtMoney(filtros.preco_min)}</Tag>
                        )}
                        {filtros.somente_nao_bloqueados && (
                            <Tag size="sm" colorScheme="gray" variant="subtle">Só não bloqueados</Tag>
                        )}
                        {filtros.amostra_aleatoria && (
                            <Tag size="sm" colorScheme="orange" variant="subtle">
                                Amostra: {fmtNum(filtros.amostra_quantidade)}
                            </Tag>
                        )}
                    </HStack>
                </>
            )}
        </Box>
    );
}

// ──────────────────────────── Modal: Detalhes Sincronização ────────────────────────────
function DetalheSincModal({ isOpen, onClose, sincId, configGlobal }) {
    const toast = useToast();
    const [sinc, setSinc] = useState(null);
    const [itens, setItens] = useState({ data: [], current_page: 1, last_page: 1 });
    const [carregandoItens, setCarregandoItens] = useState(false);
    const [busca, setBusca] = useState("");
    const [filtroStatus, setFiltroStatus] = useState("");
    const [page, setPage] = useState(1);
    const [itemSelecionado, setItemSelecionado] = useState(null);
    const pollingRef = useRef(null);

    const carregar = useCallback(async () => {
        if (!sincId) return;
        try {
            const [det, statusResp] = await Promise.all([
                comparadorDetalhesSync(sincId),
                comparadorStatusSync(sincId),
            ]);
            setSinc({ ...det.data, ...statusResp.data });
        } catch (e) {
            console.error(e);
        }
    }, [sincId]);

    const carregarItens = useCallback(async () => {
        if (!sincId) return;
        setCarregandoItens(true);
        try {
            const r = await comparadorListarItensSync(sincId, {
                page, per_page: 50,
                ...(filtroStatus && { status: filtroStatus }),
                ...(busca && { busca }),
            });
            setItens(r.data);
        } finally { setCarregandoItens(false); }
    }, [sincId, page, filtroStatus, busca]);

    useEffect(() => {
        if (!isOpen) return;
        carregar();
        carregarItens();
    }, [isOpen, carregar, carregarItens]);

    // polling enquanto status estiver em andamento
    useEffect(() => {
        if (!isOpen || !sinc) return;
        const finished = ["concluido", "falhou", "cancelado"].includes(sinc.status);
        if (finished) {
            if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
            return;
        }
        if (!pollingRef.current) {
            pollingRef.current = setInterval(() => { carregar(); carregarItens(); }, 4000);
        }
        return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
    }, [isOpen, sinc?.status, carregar, carregarItens]);

    const cancelar = async () => {
        if (!window.confirm("Cancelar esta sincronização? Itens já processados são preservados.")) return;
        try {
            await comparadorCancelarSync(sincId);
            toast({ status: "success", title: "Cancelada" });
            carregar();
        } catch (e) {
            toast({ status: "error", title: "Falha ao cancelar", description: e?.response?.data?.error });
        }
    };

    const progressoPct = sinc?.total_itens
        ? Math.round((sinc.itens_processados / sinc.total_itens) * 100)
        : 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <HStack spacing={3}>
                        <Text>Sincronização #{sincId}</Text>
                        {sinc && (
                            <Badge colorScheme={STATUS_COLOR[sinc.status]}>
                                {STATUS_LABEL[sinc.status]}
                                {["pendente", "em_processamento"].includes(sinc.status) && <Spinner size="xs" ml={2} />}
                            </Badge>
                        )}
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {sinc && (
                        <VStack spacing={4} align="stretch">
                            <SimpleGrid columns={4} spacing={3}>
                                <Stat><StatLabel>Total itens</StatLabel><StatNumber>{fmtNum(sinc.total_itens)}</StatNumber></Stat>
                                <Stat><StatLabel>Processados</StatLabel><StatNumber>{fmtNum(sinc.itens_processados)}</StatNumber></Stat>
                                <Stat><StatLabel>Com resultados</StatLabel><StatNumber>{fmtNum(sinc.itens_com_resultados)}</StatNumber></Stat>
                                <Stat><StatLabel>Sem resultados</StatLabel><StatNumber>{fmtNum(sinc.itens_sem_resultados)}</StatNumber></Stat>
                                <Stat><StatLabel>Erros</StatLabel><StatNumber>{fmtNum(sinc.itens_com_erro)}</StatNumber></Stat>
                                <Stat><StatLabel>Créditos consumidos</StatLabel><StatNumber>{fmtNum(sinc.creditos_consumidos)}</StatNumber></Stat>
                                <Stat><StatLabel>Iniciado</StatLabel><StatHelpText fontSize="sm">{fmtDateTime(sinc.iniciado_em)}</StatHelpText></Stat>
                                <Stat><StatLabel>Finalizado</StatLabel><StatHelpText fontSize="sm">{fmtDateTime(sinc.finalizado_em)}</StatHelpText></Stat>
                            </SimpleGrid>

                            <Box>
                                <HStack mb={1} fontSize="sm" justify="space-between">
                                    <Text>Progresso</Text>
                                    <Text>{progressoPct}%</Text>
                                </HStack>
                                <Progress value={progressoPct} size="sm" colorScheme="blue" hasStripe={["pendente","em_processamento"].includes(sinc.status)} isAnimated />
                            </Box>

                            {sinc.erro_msg && (
                                <Alert status="error"><AlertIcon /><AlertDescription fontSize="sm">{sinc.erro_msg}</AlertDescription></Alert>
                            )}

                            <HStack>
                                <Button size="sm" leftIcon={<DownloadIcon />} as="a"
                                        href={comparadorExportarCsvUrl(sincId)} target="_blank">
                                    Exportar CSV
                                </Button>
                                {["pendente", "em_processamento"].includes(sinc.status) && (
                                    <Button size="sm" colorScheme="orange" onClick={cancelar}>Cancelar</Button>
                                )}
                                <Button size="sm" leftIcon={<RepeatIcon />} onClick={() => { carregar(); carregarItens(); }}>Atualizar</Button>
                            </HStack>

                            <ConfigsUsadasBlock sinc={sinc} configGlobal={configGlobal} />

                            <Divider />

                            <HStack>
                                <Input placeholder="Buscar produto/código…" value={busca} onChange={e => setBusca(e.target.value)} size="sm" w="280px" />
                                <Select size="sm" w="220px" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                                    <option value="">Todos status</option>
                                    <option value="concluido">Concluído</option>
                                    <option value="sem_resultados">Sem resultados</option>
                                    <option value="erro">Erro</option>
                                    <option value="pendente">Pendente</option>
                                    <option value="processando">Processando</option>
                                </Select>
                                <Button size="sm" onClick={() => { setPage(1); carregarItens(); }}>Filtrar</Button>
                            </HStack>

                            <Box overflowX="auto">
                                <Table size="sm" variant="striped">
                                    <Thead>
                                        <Tr>
                                            <Th>#</Th>
                                            <Th>Código</Th>
                                            <Th>Produto</Th>
                                            <Th>Status</Th>
                                            <Th isNumeric>Resultados</Th>
                                            <Th isNumeric>Nosso ML</Th>
                                            <Th isNumeric>Nosso Site</Th>
                                            <Th isNumeric>Min mercado</Th>
                                            <Th isNumeric>Média</Th>
                                            <Th isNumeric>Diff ML %</Th>
                                            <Th isNumeric>Diff Site %</Th>
                                            <Th></Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {carregandoItens && (
                                            <Tr><Td colSpan={12} textAlign="center"><Spinner size="sm" /></Td></Tr>
                                        )}
                                        {!carregandoItens && itens.data?.map(it => {
                                            const diffMl = (it.nosso_preco && it.preco_medio_concorrente)
                                                ? ((it.nosso_preco - it.preco_medio_concorrente) / it.preco_medio_concorrente) * 100
                                                : null;
                                            const diffSite = (it.nosso_preco_site && it.preco_medio_concorrente)
                                                ? ((it.nosso_preco_site - it.preco_medio_concorrente) / it.preco_medio_concorrente) * 100
                                                : null;
                                            return (
                                                <Tr key={it.id} _hover={{ bg: "blue.50", cursor: "pointer" }} onClick={() => setItemSelecionado(it)}>
                                                    <Td fontSize="xs">{it.id}</Td>
                                                    <Td fontSize="xs">{it.codigo_global}</Td>
                                                    <Td fontSize="xs" maxW="280px" isTruncated title={it.produto_nome}>{it.produto_nome}</Td>
                                                    <Td><Badge colorScheme={ITEM_STATUS_COLOR[it.status]}>{it.status}</Badge></Td>
                                                    <Td isNumeric fontSize="xs">{it.resultados_validos}/{it.total_resultados}</Td>
                                                    <Td isNumeric fontSize="xs">{fmtMoney(it.nosso_preco)}</Td>
                                                    <Td isNumeric fontSize="xs">{fmtMoney(it.nosso_preco_site)}</Td>
                                                    <Td isNumeric fontSize="xs">{fmtMoney(it.menor_preco_concorrente)}</Td>
                                                    <Td isNumeric fontSize="xs">{fmtMoney(it.preco_medio_concorrente)}</Td>
                                                    <Td isNumeric fontSize="xs" color={diffMl > 0 ? "red.600" : (diffMl < 0 ? "green.600" : "gray.600")}>
                                                        {diffMl !== null ? fmtPct(diffMl) : "—"}
                                                    </Td>
                                                    <Td isNumeric fontSize="xs" color={diffSite > 0 ? "red.600" : (diffSite < 0 ? "green.600" : "gray.600")}>
                                                        {diffSite !== null ? fmtPct(diffSite) : "—"}
                                                    </Td>
                                                    <Td onClick={(e) => e.stopPropagation()}>
                                                        <Button size="xs" colorScheme="blue" variant="ghost" onClick={() => setItemSelecionado(it)}>
                                                            Detalhes
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                        {!carregandoItens && (!itens.data || itens.data.length === 0) && (
                                            <Tr><Td colSpan={12} textAlign="center" color="gray.500">Sem itens</Td></Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>

                            {itens.last_page > 1 && (
                                <HStack justify="center">
                                    <Button size="xs" onClick={() => { setPage(p => Math.max(1, p - 1)); }} isDisabled={page <= 1}>Anterior</Button>
                                    <Text fontSize="xs">Página {itens.current_page} de {itens.last_page}</Text>
                                    <Button size="xs" onClick={() => { setPage(p => p + 1); }} isDisabled={page >= itens.last_page}>Próxima</Button>
                                </HStack>
                            )}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>

            <ItemDetalheModal
                isOpen={!!itemSelecionado}
                onClose={() => setItemSelecionado(null)}
                sincId={sincId}
                item={itemSelecionado}
            />
        </Modal>
    );
}

// ──────────────────────────── Tab: Dashboard ────────────────────────────
function KpiCard({ label, value, sub, accent }) {
    const accentColor = accent || "gray";
    return (
        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" borderTop="3px solid"
             borderColor={`${accentColor}.400`}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.5px" fontWeight="600">
                {label}
            </Text>
            <Text fontSize="2xl" fontWeight="700" color="gray.800" mt={1} lineHeight={1.2}>
                {value}
            </Text>
            {sub && <Text fontSize="xs" color="gray.500" mt={1}>{sub}</Text>}
        </Box>
    );
}

function BarraDistribuicao({ abaixo, iguais, acima, totalLabel, totalValue, pctAbaixo, pctAcima }) {
    const pctIguais = Math.max(0, 100 - Number(pctAbaixo || 0) - Number(pctAcima || 0));
    return (
        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
            <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" fontWeight="600" color="gray.700">{totalLabel}</Text>
                <Text fontSize="xs" color="gray.500"><b>{fmtNum(totalValue)}</b> itens</Text>
            </HStack>

            {totalValue > 0 ? (
                <>
                    <Flex h="30px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.100">
                        <Box bg="green.400" w={`${pctAbaixo}%`} title={`${fmtPct(pctAbaixo)} abaixo`} />
                        <Box bg="gray.300"  w={`${pctIguais}%`} title={`${fmtPct(pctIguais)} iguais`} />
                        <Box bg="red.400"   w={`${pctAcima}%`}  title={`${fmtPct(pctAcima)} acima`} />
                    </Flex>
                    <SimpleGrid columns={3} spacing={2} mt={3}>
                        <Box>
                            <HStack spacing={1} mb={1}>
                                <Box w="8px" h="8px" bg="green.400" borderRadius="full" />
                                <Text fontSize="xs" color="gray.500">Mais barato</Text>
                            </HStack>
                            <Text fontSize="md" fontWeight="600" color="green.600">{fmtNum(abaixo)}</Text>
                            <Text fontSize="xs" color="gray.500">{fmtPct(pctAbaixo)}</Text>
                        </Box>
                        <Box>
                            <HStack spacing={1} mb={1}>
                                <Box w="8px" h="8px" bg="gray.300" borderRadius="full" />
                                <Text fontSize="xs" color="gray.500">Igual</Text>
                            </HStack>
                            <Text fontSize="md" fontWeight="600" color="gray.700">{fmtNum(iguais ?? 0)}</Text>
                            <Text fontSize="xs" color="gray.500">{fmtPct(pctIguais)}</Text>
                        </Box>
                        <Box>
                            <HStack spacing={1} mb={1}>
                                <Box w="8px" h="8px" bg="red.400" borderRadius="full" />
                                <Text fontSize="xs" color="gray.500">Mais caro</Text>
                            </HStack>
                            <Text fontSize="md" fontWeight="600" color="red.600">{fmtNum(acima)}</Text>
                            <Text fontSize="xs" color="gray.500">{fmtPct(pctAcima)}</Text>
                        </Box>
                    </SimpleGrid>
                </>
            ) : (
                <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>Sem itens comparados no período</Text>
            )}
        </Box>
    );
}

function TopProdutosTable({ titulo, accent, items, precoColuna, diffPositivo }) {
    return (
        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
            <Box p={4} pb={3} borderBottom="1px solid" borderColor="gray.100">
                <HStack>
                    <Box w="3px" h="14px" bg={`${accent}.400`} borderRadius="sm" />
                    <Heading size="xs" color="gray.700">{titulo}</Heading>
                </HStack>
            </Box>
            <Box overflowX="auto">
                <Table size="sm">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th fontSize="10px">Cód</Th>
                            <Th fontSize="10px">Produto</Th>
                            <Th isNumeric fontSize="10px">{precoColuna}</Th>
                            <Th isNumeric fontSize="10px">Mkt</Th>
                            <Th isNumeric fontSize="10px">Diff</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {items.map(p => (
                            <Tr key={p.id}>
                                <Td fontSize="xs" color="gray.500">{p.codigo_global}</Td>
                                <Td fontSize="xs" maxW="220px" isTruncated title={p.produto_nome}>{p.produto_nome}</Td>
                                <Td isNumeric fontSize="xs" fontWeight="600">
                                    {precoColuna === "Site" ? fmtMoney(p.nosso_preco_site) : fmtMoney(p.nosso_preco)}
                                </Td>
                                <Td isNumeric fontSize="xs" color="gray.600">{fmtMoney(p.preco_medio_concorrente)}</Td>
                                <Td isNumeric fontSize="xs" fontWeight="600" color={diffPositivo ? "red.600" : "green.600"}>
                                    {diffPositivo ? "+" : "−"}{fmtPct(p.diff_pct)}
                                </Td>
                            </Tr>
                        ))}
                        {!items.length && <Tr><Td colSpan={5} color="gray.400" textAlign="center" py={6} fontSize="xs">Sem dados</Td></Tr>}
                    </Tbody>
                </Table>
            </Box>
        </Box>
    );
}

function DashboardTab({ onAbrirSinc }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataInicio, setDataInicio] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 30);
        return d.toISOString().substring(0, 10);
    });
    const [dataFim, setDataFim] = useState(() => new Date().toISOString().substring(0, 10));

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await comparadorDashboard({ data_inicio: dataInicio, data_fim: dataFim });
            setData(r.data);
        } finally { setLoading(false); }
    }, [dataInicio, dataFim]);

    useEffect(() => { carregar(); }, [carregar]);

    if (loading && !data) return <HStack justify="center" py={10}><Spinner /></HStack>;
    if (!data) return <Text color="gray.500">Sem dados.</Text>;

    const maxFonte = Math.max(1, ...(data.distribuicao_fontes || []).map(d => Number(d.total)));
    const maxDia = Math.max(1, ...(data.sincronizacoes_por_dia || []).map(d => Number(d.total)));

    return (
        <VStack spacing={5} align="stretch">
            {/* Filtro de período */}
            <Box bg="white" p={3} borderRadius="lg" boxShadow="sm">
                <HStack spacing={3} align="end" flexWrap="wrap">
                    <FormControl maxW="170px">
                        <FormLabel fontSize="xs" color="gray.500" mb={1}>Período de</FormLabel>
                        <Input type="date" size="sm" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
                    </FormControl>
                    <FormControl maxW="170px">
                        <FormLabel fontSize="xs" color="gray.500" mb={1}>Até</FormLabel>
                        <Input type="date" size="sm" value={dataFim} onChange={e => setDataFim(e.target.value)} />
                    </FormControl>
                    <Button size="sm" colorScheme="blue" onClick={carregar} leftIcon={<RepeatIcon />} isLoading={loading}>
                        Atualizar
                    </Button>
                    <Box flex={1} />
                    <Text fontSize="xs" color="gray.500">
                        {data.periodo.inicio} → {data.periodo.fim}
                    </Text>
                </HStack>
            </Box>

            {/* KPIs principais */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                <KpiCard
                    label="Sincronizações"
                    value={fmtNum(data.resumo.total_sincronizacoes)}
                    accent="blue"
                />
                <KpiCard
                    label="Itens comparados"
                    value={fmtNum(data.resumo.total_itens_comparados)}
                    sub={data.resumo.total_com_site ? `${fmtNum(data.resumo.total_com_site)} com preço Site` : null}
                    accent="purple"
                />
                <KpiCard
                    label="Créditos Serper"
                    value={fmtNum(data.resumo.total_creditos)}
                    accent="orange"
                />
                <KpiCard
                    label="Duração média"
                    value={fmtDuration(data.resumo.duracao_media_seg)}
                    accent="green"
                />
            </SimpleGrid>

            {/* Em andamento */}
            {data.em_andamento?.length > 0 && (
                <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" borderLeft="3px solid" borderColor="blue.400">
                    <HStack mb={3}>
                        <Activity size={16} color="#3182ce" />
                        <Heading size="sm" color="gray.700">Em andamento</Heading>
                        <Badge colorScheme="blue" variant="subtle">{data.em_andamento.length}</Badge>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {data.em_andamento.map(s => {
                            const pct = s.total_itens ? Math.round((s.itens_processados / s.total_itens) * 100) : 0;
                            return (
                                <Box key={s.id} p={3} borderRadius="md" bg="gray.50" cursor="pointer"
                                     _hover={{ bg: "blue.50", transform: "translateY(-1px)" }} transition="all 0.15s"
                                     onClick={() => onAbrirSinc(s.id)}>
                                    <HStack justify="space-between" mb={2}>
                                        <HStack spacing={2}>
                                            <Badge colorScheme={STATUS_COLOR[s.status]} fontSize="10px">{STATUS_LABEL[s.status]}</Badge>
                                            <Text fontSize="sm" fontWeight="600">#{s.id}</Text>
                                            <Text fontSize="xs" color="gray.500">· {s.tipo}</Text>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">{pct}%</Text>
                                    </HStack>
                                    <Progress value={pct} size="xs" colorScheme="blue" hasStripe isAnimated borderRadius="full" />
                                    <HStack justify="space-between" mt={2}>
                                        <Text fontSize="xs" color="gray.500">
                                            {fmtNum(s.itens_processados)} / {fmtNum(s.total_itens)} itens
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {fmtDateTime(s.iniciado_em || s.created_at)}
                                        </Text>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </SimpleGrid>
                </Box>
            )}

            {/* Posicionamento de preços */}
            <Box>
                <Heading size="sm" mb={3} color="gray.700">Posicionamento de preços</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <BarraDistribuicao
                        totalLabel="Mercado Livre vs concorrência"
                        totalValue={data.resumo.total_itens_comparados}
                        abaixo={data.resumo.abaixo_media}
                        iguais={data.resumo.iguais}
                        acima={data.resumo.acima_media}
                        pctAbaixo={data.resumo.pct_abaixo}
                        pctAcima={data.resumo.pct_acima}
                    />
                    <BarraDistribuicao
                        totalLabel="Site (calculado) vs concorrência"
                        totalValue={data.resumo.total_com_site}
                        abaixo={data.resumo.abaixo_media_site}
                        iguais={
                            (data.resumo.total_com_site || 0)
                            - (data.resumo.abaixo_media_site || 0)
                            - (data.resumo.acima_media_site || 0)
                        }
                        acima={data.resumo.acima_media_site}
                        pctAbaixo={data.resumo.pct_abaixo_site}
                        pctAcima={data.resumo.pct_acima_site}
                    />
                </SimpleGrid>
            </Box>

            {/* Top produtos */}
            <Box>
                <Heading size="sm" mb={3} color="gray.700">Top produtos vs mercado</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <TopProdutosTable
                        titulo="ML — mais caros que o mercado"
                        accent="red"
                        items={data.top_mais_caros_vs_mercado}
                        precoColuna="ML"
                        diffPositivo={true}
                    />
                    <TopProdutosTable
                        titulo="ML — mais baratos que o mercado"
                        accent="green"
                        items={data.top_mais_baratos_vs_mercado}
                        precoColuna="ML"
                        diffPositivo={false}
                    />
                    <TopProdutosTable
                        titulo="Site — mais caros que o mercado"
                        accent="red"
                        items={data.top_mais_caros_site_vs_mercado || []}
                        precoColuna="Site"
                        diffPositivo={true}
                    />
                    <TopProdutosTable
                        titulo="Site — mais baratos que o mercado"
                        accent="green"
                        items={data.top_mais_baratos_site_vs_mercado || []}
                        precoColuna="Site"
                        diffPositivo={false}
                    />
                </SimpleGrid>
            </Box>

            {/* Distribuições */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                    <Heading size="sm" mb={3} color="gray.700">Lojas concorrentes (top 15)</Heading>
                    {(data.distribuicao_fontes || []).length === 0 ? (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>Sem dados</Text>
                    ) : (
                        <VStack align="stretch" spacing={1.5}>
                            {data.distribuicao_fontes.map(d => {
                                const pct = (Number(d.total) / maxFonte) * 100;
                                return (
                                    <HStack key={d.source} spacing={3}>
                                        <Text fontSize="xs" minW="120px" maxW="120px" isTruncated title={d.source}>
                                            {d.source}
                                        </Text>
                                        <Box flex={1} h="14px" bg="gray.100" borderRadius="sm" position="relative">
                                            <Box h="100%" w={`${pct}%`} bg="blue.300" borderRadius="sm" />
                                        </Box>
                                        <Text fontSize="xs" minW="50px" textAlign="right" fontWeight="600">
                                            {fmtNum(d.total)}
                                        </Text>
                                    </HStack>
                                );
                            })}
                        </VStack>
                    )}
                </Box>
                <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                    <Heading size="sm" mb={3} color="gray.700">Atividade por dia</Heading>
                    {(data.sincronizacoes_por_dia || []).length === 0 ? (
                        <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>Sem dados</Text>
                    ) : (
                        <VStack align="stretch" spacing={1.5}>
                            {data.sincronizacoes_por_dia.map(d => {
                                const pct = (Number(d.total) / maxDia) * 100;
                                return (
                                    <HStack key={d.dia} spacing={3}>
                                        <Text fontSize="xs" minW="90px" maxW="90px" color="gray.600">
                                            {d.dia}
                                        </Text>
                                        <Box flex={1} h="14px" bg="gray.100" borderRadius="sm">
                                            <Box h="100%" w={`${pct}%`} bg="purple.300" borderRadius="sm" />
                                        </Box>
                                        <Text fontSize="xs" minW="40px" textAlign="right" fontWeight="600">
                                            {d.total}
                                        </Text>
                                        <Text fontSize="10px" color="gray.500" minW="60px" textAlign="right">
                                            {fmtNum(d.creditos)} cr.
                                        </Text>
                                    </HStack>
                                );
                            })}
                        </VStack>
                    )}
                </Box>
            </SimpleGrid>
        </VStack>
    );
}

// ──────────────────────────── Tab: Sincronizações ────────────────────────────
function SyncsTab({ onAbrirSinc, refreshSignal }) {
    const [data, setData] = useState({ data: [], current_page: 1, last_page: 1 });
    const [loading, setLoading] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState("");
    const [page, setPage] = useState(1);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await comparadorListarSyncs({
                page, per_page: 25,
                ...(filtroStatus && { status: filtroStatus }),
            });
            setData(r.data);
        } finally { setLoading(false); }
    }, [page, filtroStatus]);

    useEffect(() => { carregar(); }, [carregar, refreshSignal]);

    // auto-refresh enquanto houver sync em andamento
    const temAndamento = data.data?.some(s => ["pendente", "em_processamento"].includes(s.status));
    useEffect(() => {
        if (!temAndamento) return;
        const t = setInterval(carregar, 5000);
        return () => clearInterval(t);
    }, [temAndamento, carregar]);

    return (
        <VStack spacing={4} align="stretch">
            <HStack>
                <Select size="sm" w="220px" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                    <option value="">Todos status</option>
                    <option value="pendente">Pendente</option>
                    <option value="em_processamento">Processando</option>
                    <option value="concluido">Concluído</option>
                    <option value="falhou">Falhou</option>
                    <option value="cancelado">Cancelado</option>
                </Select>
                <Button size="sm" onClick={() => { setPage(1); carregar(); }} leftIcon={<RepeatIcon />}>Atualizar</Button>
                {loading && <Spinner size="sm" />}
            </HStack>

            <Box bg="white" borderRadius="md" boxShadow="sm" overflowX="auto">
                <Table size="sm">
                    <Thead>
                        <Tr>
                            <Th>#</Th>
                            <Th>Status</Th>
                            <Th>Tipo</Th>
                            <Th>Referência</Th>
                            <Th>Progresso</Th>
                            <Th isNumeric>Itens</Th>
                            <Th isNumeric>Créditos</Th>
                            <Th>Usuário</Th>
                            <Th>Criada</Th>
                            <Th>Iniciada</Th>
                            <Th>Finalizada</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {data.data?.map(s => {
                            const pct = s.total_itens ? Math.round((s.itens_processados / s.total_itens) * 100) : 0;
                            return (
                                <Tr key={s.id} _hover={{ bg: "gray.50" }}>
                                    <Td>{s.id}</Td>
                                    <Td><Badge colorScheme={STATUS_COLOR[s.status]}>{STATUS_LABEL[s.status]}</Badge></Td>
                                    <Td fontSize="xs">{s.tipo}</Td>
                                    <Td fontSize="xs">{s.referencia_marca || s.referencia_id || "—"}</Td>
                                    <Td minW="160px">
                                        <Progress value={pct} size="xs" colorScheme="blue" />
                                        <Text fontSize="xs" color="gray.500">{pct}% · {s.itens_processados}/{s.total_itens}</Text>
                                    </Td>
                                    <Td isNumeric fontSize="xs">{s.total_itens}</Td>
                                    <Td isNumeric fontSize="xs">{s.creditos_consumidos}</Td>
                                    <Td fontSize="xs">{s.usuario?.name || "—"}</Td>
                                    <Td fontSize="xs">{fmtDateTime(s.created_at)}</Td>
                                    <Td fontSize="xs">{fmtDateTime(s.iniciado_em)}</Td>
                                    <Td fontSize="xs">{fmtDateTime(s.finalizado_em)}</Td>
                                    <Td>
                                        <Button size="xs" onClick={() => onAbrirSinc(s.id)}>Detalhes</Button>
                                    </Td>
                                </Tr>
                            );
                        })}
                        {(!data.data || data.data.length === 0) && !loading && (
                            <Tr><Td colSpan={12} textAlign="center" color="gray.500">Sem sincronizações</Td></Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>

            {data.last_page > 1 && (
                <HStack justify="center">
                    <Button size="xs" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Anterior</Button>
                    <Text fontSize="xs">Página {data.current_page} de {data.last_page}</Text>
                    <Button size="xs" onClick={() => setPage(p => p + 1)} isDisabled={page >= data.last_page}>Próxima</Button>
                </HStack>
            )}
        </VStack>
    );
}

// ──────────────────────────── Tab: Configurações ────────────────────────────
function ConfigTab({ config, onSave }) {
    const toast = useToast();
    const [form, setForm] = useState(config || {});
    const [salvando, setSalvando] = useState(false);

    useEffect(() => { if (config) setForm(config); }, [config]);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const inRange = (v, min, max) => {
        const n = Number(v);
        return isFinite(n) && n >= min && n <= max;
    };

    const salvar = async () => {
        // Validacao client-side antes de mandar
        const errs = [];
        if (!inRange(form.max_resultados_serper, 1, 100))
            errs.push("Máx resultados Serper deve estar entre 1 e 100");
        if (!inRange(form.similaridade_nome_min, 0, 100))
            errs.push("Similaridade do nome deve estar entre 0 e 100");
        if (!inRange(form.similaridade_marca_min, 0, 100))
            errs.push("Similaridade da marca deve estar entre 0 e 100");
        if (!inRange(form.max_itens_por_sync, 1, 50000))
            errs.push("Máx itens por sync deve estar entre 1 e 50000");
        if (!inRange(form.min_itens_por_sync, 1, 50000))
            errs.push("Mín itens por sync deve ser >= 1");
        if (Number(form.min_itens_por_sync) > Number(form.max_itens_por_sync))
            errs.push("Mín itens não pode ser maior que máx");
        if (!inRange(form.filtro_quantidade_min, 0, 1e9))
            errs.push("Quantidade mínima deve ser >= 0");
        if (form.filtro_preco_min !== "" && form.filtro_preco_min !== null && !inRange(form.filtro_preco_min, 0, 1e9))
            errs.push("Preço mínimo deve ser >= 0 ou vazio");
        if (form.outlier_aplicar) {
            if (Number(form.outlier_max_multiplicador) < 0 || !isFinite(Number(form.outlier_max_multiplicador)))
                errs.push("Multiplicador máx do outlier deve ser >= 0");
            if (Number(form.outlier_min_divisor) < 0 || !isFinite(Number(form.outlier_min_divisor)))
                errs.push("Divisor mín do outlier deve ser >= 0");
        }

        if (errs.length) {
            toast({ status: "warning", title: "Valores inválidos", description: errs.join("; ") });
            return;
        }

        setSalvando(true);
        try {
            const payload = {
                ...form,
                max_resultados_serper: Number(form.max_resultados_serper),
                similaridade_nome_min: Number(form.similaridade_nome_min),
                similaridade_marca_min: Number(form.similaridade_marca_min),
                max_itens_por_sync: Number(form.max_itens_por_sync),
                min_itens_por_sync: Number(form.min_itens_por_sync),
                filtro_quantidade_min: Number(form.filtro_quantidade_min),
                filtro_preco_min: form.filtro_preco_min === "" || form.filtro_preco_min === null ? null : Number(form.filtro_preco_min),
                dominios_bloqueados: parseDominios(form.dominios_bloqueados),
                outlier_aplicar: !!form.outlier_aplicar,
                outlier_max_multiplicador: Number(form.outlier_max_multiplicador ?? 3),
                outlier_min_divisor: Number(form.outlier_min_divisor ?? 3),
            };
            const r = await comparadorSaveConfig(payload);
            toast({ status: "success", title: "Configurações salvas" });
            onSave?.(r.data);
        } catch (e) {
            toast({ status: "error", title: "Falha ao salvar", description: e?.response?.data?.message || e.message });
        } finally { setSalvando(false); }
    };

    if (!config) return <Spinner />;

    return (
        <Box maxW="720px" bg="white" p={5} borderRadius="md" boxShadow="sm">
            <Heading size="sm" mb={4}>Configurações globais (defaults)</Heading>
            <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                    <FormLabel fontSize="sm">Máx resultados Serper por busca (1–100)</FormLabel>
                    <Input type="number" min={1} max={100} step={1} value={form.max_resultados_serper ?? ""} onChange={e => set("max_resultados_serper", e.target.value)} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="sm">Similaridade mínima do nome (0–100%)</FormLabel>
                    <Input type="number" min={0} max={100} step="0.01" value={form.similaridade_nome_min ?? ""} onChange={e => set("similaridade_nome_min", e.target.value)} />
                </FormControl>
                <FormControl isDisabled={!form.exigir_marca_match}>
                    <FormLabel fontSize="sm">Similaridade mínima da marca (0–100%)</FormLabel>
                    <Input type="number" min={0} max={100} step="0.01" value={form.similaridade_marca_min ?? ""} onChange={e => set("similaridade_marca_min", e.target.value)} />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                        {form.exigir_marca_match
                            ? "Aplicado quando \"Exigir marca\" está ativo."
                            : "Sem efeito — qualquer marca é aceita."}
                    </Text>
                </FormControl>
                <FormControl display="flex" alignItems="center" pt={6}>
                    <Switch isChecked={!!form.exigir_marca_match} onChange={e => set("exigir_marca_match", e.target.checked)} mr={2} />
                    <FormLabel fontSize="sm" m={0}>Exigir marca igual / similar no resultado</FormLabel>
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="sm">Máx itens por sincronização (1–50000)</FormLabel>
                    <Input type="number" min={1} max={50000} step={1} value={form.max_itens_por_sync ?? ""} onChange={e => set("max_itens_por_sync", e.target.value)} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="sm">Mín itens por sincronização (≥ 1)</FormLabel>
                    <Input type="number" min={1} step={1} value={form.min_itens_por_sync ?? ""} onChange={e => set("min_itens_por_sync", e.target.value)} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="sm">Filtro padrão: quantidade mínima (≥ 0)</FormLabel>
                    <Input type="number" min={0} step={1} value={form.filtro_quantidade_min ?? ""} onChange={e => set("filtro_quantidade_min", e.target.value)} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="sm">Filtro padrão: preço mínimo (R$)</FormLabel>
                    <Input type="number" min={0} step="0.01" value={form.filtro_preco_min ?? ""} onChange={e => set("filtro_preco_min", e.target.value)} placeholder="vazio = sem filtro" />
                </FormControl>
                <FormControl display="flex" alignItems="center" pt={6}>
                    <Switch isChecked={!!form.permite_amostra_aleatoria} onChange={e => set("permite_amostra_aleatoria", e.target.checked)} mr={2} />
                    <FormLabel fontSize="sm" m={0}>Permitir amostra aleatória</FormLabel>
                </FormControl>
            </SimpleGrid>

            <Divider my={5} />
            <Heading size="xs" mb={3} color="gray.700">Filtros de qualidade dos resultados</Heading>

            <FormControl mb={4}>
                <FormLabel fontSize="sm">Domínios bloqueados (um por linha · ignora resultados dessas lojas)</FormLabel>
                <Input
                    as="textarea"
                    rows={3}
                    fontFamily="mono"
                    fontSize="sm"
                    minH="84px"
                    py={2}
                    value={stringifyDominios(form.dominios_bloqueados)}
                    onChange={e => set("dominios_bloqueados", parseDominios(e.target.value))}
                    placeholder="tagaautopartes.com.br"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                    Domínios cujos resultados não devem ser considerados (a comparação ignora a própria loja, por exemplo).
                </Text>
            </FormControl>

            <FormControl display="flex" alignItems="center" mb={3}>
                <Switch isChecked={!!form.outlier_aplicar} onChange={e => set("outlier_aplicar", e.target.checked)} mr={2} />
                <FormLabel fontSize="sm" m={0}>Aplicar filtro de outlier (descarta preços absurdos)</FormLabel>
            </FormControl>
            <SimpleGrid columns={2} spacing={4}>
                <FormControl isDisabled={!form.outlier_aplicar}>
                    <FormLabel fontSize="sm">Multiplicador máximo (descarta se preço &gt; N × referência)</FormLabel>
                    <Input type="number" min={0} step="0.1"
                        value={form.outlier_max_multiplicador ?? ""}
                        onChange={e => set("outlier_max_multiplicador", e.target.value)} />
                </FormControl>
                <FormControl isDisabled={!form.outlier_aplicar}>
                    <FormLabel fontSize="sm">Divisor mínimo (descarta se preço &lt; referência ÷ N)</FormLabel>
                    <Input type="number" min={0} step="0.1"
                        value={form.outlier_min_divisor ?? ""}
                        onChange={e => set("outlier_min_divisor", e.target.value)} />
                </FormControl>
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={2}>
                Referência usada: <b>nosso preço ML</b>; se ausente, usa o preço Site; se ausente, usa a mediana dos válidos.
                Use <b>0</b> em qualquer dos lados para desativar aquela borda. Ex: 3/3 descarta tudo &gt; 3× ou &lt; 1/3 da referência.
            </Text>

            <Button colorScheme="blue" mt={5} onClick={salvar} isLoading={salvando}>Salvar configurações</Button>
        </Box>
    );
}

// ──────────────────────────── Página principal ────────────────────────────
export default function ComparadorPrecos() {
    const novaSinc = useDisclosure();
    const detalhe = useDisclosure();
    const [sincSelId, setSincSelId] = useState(null);
    const [config, setConfig] = useState(null);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const bg = useColorModeValue("gray.50", "gray.900");

    useEffect(() => {
        comparadorGetConfig().then(r => setConfig(r.data)).catch(() => {});
    }, []);

    const abrirSinc = (id) => { setSincSelId(id); detalhe.onOpen(); };

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg={bg} p={{ base: 3, md: 6 }}>
            <ScrollToTop />
            <Flex mb={4} align="center" wrap="wrap" gap={2}>
                <Heading size="md">Comparador de Preços</Heading>
                <Box flex={1} />
                <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={novaSinc.onOpen}>
                    Nova Sincronização
                </Button>
            </Flex>

            <Tabs variant="enclosed-colored" colorScheme="blue" bg="white" borderRadius="md" boxShadow="sm">
                <TabList>
                    <Tab><HStack><BarChart3 size={16} /><Text>Dashboard</Text></HStack></Tab>
                    <Tab><HStack><ListChecks size={16} /><Text>Sincronizações</Text></HStack></Tab>
                    <Tab><HStack><SettingsIc size={16} /><Text>Configurações</Text></HStack></Tab>
                </TabList>
                <TabPanels>
                    <TabPanel><DashboardTab onAbrirSinc={abrirSinc} /></TabPanel>
                    <TabPanel><SyncsTab onAbrirSinc={abrirSinc} refreshSignal={refreshSignal} /></TabPanel>
                    <TabPanel><ConfigTab config={config} onSave={(c) => setConfig(c)} /></TabPanel>
                </TabPanels>
            </Tabs>

            {novaSinc.isOpen && (
                <NovaSincModal
                    isOpen={novaSinc.isOpen}
                    onClose={novaSinc.onClose}
                    configGlobal={config}
                    onCreated={(s) => { setRefreshSignal(x => x + 1); setSincSelId(s.sincronizacao_id); detalhe.onOpen(); }}
                />
            )}
            {detalhe.isOpen && (
                <DetalheSincModal
                    isOpen={detalhe.isOpen}
                    onClose={() => { detalhe.onClose(); setRefreshSignal(x => x + 1); }}
                    sincId={sincSelId}
                    configGlobal={config}
                />
            )}
        </Box>
    );
}
